import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import CallFormCard from "@/components/CallFormCard";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Sheet, Star, Lock } from "lucide-react";
import { fmtDateTime } from "@/lib/date";
import { AdminSync } from "@/pages/Vetting";
import RichBlock, { Unavailable } from "@/components/vetting/RichBlock";
import { PrimaryZone } from "@/components/vetting/ResourceCard";
import { Expandable } from "@/components/network/parts";

const plain = (runs) => (runs || []).map((r) => r.t).join("").trim();

function CrossLinks({ links }) {
  if (!links?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {links.map((c) => (
        <Link key={c.to} to={c.to} data-testid={`lead-cross-${c.to.replace(/\//g, "-")}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A961] text-[#0A1F44] text-xs font-semibold px-3 py-2 hover:bg-[#C9A961]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
          {c.label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      ))}
    </div>
  );
}

function TopicBody({ t, sid }) {
  return (
    <div className="space-y-3">
      {t.placeholder ? <Unavailable text={plain(t.body)} testid={`lead-${sid}-${t.id}-placeholder`} />
        : <RichBlock runs={t.body} testid={`lead-${sid}-${t.id}-body`} />}
      <CrossLinks links={t.cross_links} />
    </div>
  );
}

function Topic({ t, sid, acc, label, className, testid }) {
  return (
    <div id={t.id} className="scroll-mt-24">
      <Expandable label={label || t.label} testid={testid || `lead-topic-${sid}-${t.id}`} className={className}
                  open={acc.isOpen(t.id)} onToggle={() => acc.toggle(t.id)}>
        <PrimaryZone on={acc.isPrimary(t.id)}><TopicBody t={t} sid={sid} /></PrimaryZone>
      </Expandable>
    </div>
  );
}

const startParts = (sec) => {
  const star = sec.topics.find((t) => /VETTING/i.test(t.label));
  return { star, rest: sec.topics.filter((t) => t !== star && !/^WHERE DO I GO/i.test(t.label)) };
};

// Source order of the collapsible topic blocks shown in a section (excludes headers/navigation).
const topicOrder = (sec) => {
  if (sec.id === "start-here") { const { star, rest } = startParts(sec); return [star, ...rest].filter(Boolean).map((t) => t.id); }
  return sec.groups ? sec.groups.flatMap((g) => g.topics) : sec.topics.map((t) => t.id);
};

function StartHere({ sec, acc }) {
  const { star, rest } = startParts(sec);
  return (
    <div className="space-y-3">
      {star && (
        <div data-testid="lead-vetting-callout">
          <Topic t={star} sid="start-here" acc={acc} testid={`lead-start-${star.id}`} className="!bg-[#C9A961]/15 !border-[#C9A961]"
                 label={<span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-[#C9A961]" />{star.label.replace(/^⭐\s*/, "")}</span>} />
        </div>
      )}
      {rest.map((t) => <Topic key={t.id} t={t} sid="start-here" acc={acc} testid={`lead-start-${t.id}`} />)}
      <section className="space-y-2" data-testid="lead-where">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">Where do I go?</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {sec.where.map((w, i) => (
            <Link key={i} to={`/leadership/${w.section}`} data-testid={`lead-where-${w.section}`}
                  className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-[#0A1F44] hover:border-[#C9A961] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
              <span className="flex-1">{w.text}</span><ArrowRight className="w-4 h-4 mt-0.5 text-[#C9A961] shrink-0" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Topics({ sec, acc }) {
  if (!sec.groups) return <div className="space-y-2">{sec.topics.map((t) => <Topic key={t.id} t={t} sid={sec.id} acc={acc} />)}</div>;
  const byId = Object.fromEntries(sec.topics.map((t) => [t.id, t]));
  const flow = sec.topics.find((t) => /SIMPLE COACHING FLOW/i.test(t.label));
  return (
    <div className="space-y-6">
      {flow && <Link to={`#${flow.id}`} data-testid="lead-jump-coaching-flow" className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1F44] text-white text-xs font-semibold px-3 py-2">Jump to Simple Coaching Flow <ArrowRight className="w-3.5 h-3.5" /></Link>}
      {sec.groups.map((g) => (
        <section key={g.label} className="space-y-2" data-testid={`lead-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <h3 className="font-serif text-xl text-[#0A1F44]">{g.label}</h3>
          {g.topics.map((id) => <Topic key={id} t={byId[id]} sid={sec.id} acc={acc} />)}
        </section>
      ))}
    </div>
  );
}

function SectionNav({ data, current }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="lg:hidden">
        <label htmlFor="lead-section-select" className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Choose a section</label>
        <Select value={current} onValueChange={(v) => navigate(`/leadership/${v}`)}>
          <SelectTrigger id="lead-section-select" className="mt-1 h-11 bg-white" data-testid="lead-section-select"><SelectValue /></SelectTrigger>
          <SelectContent>{data.stages.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`lead-select-${s.id}`}>{s.nav}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <nav className="hidden lg:block sticky top-24 self-start space-y-0.5" aria-label="Leadership sections" data-testid="lead-nav">
        {data.stages.map((s) => (
          <Link key={s.id} to={`/leadership/${s.id}`} data-testid={`lead-nav-${s.id}`} aria-current={s.id === current ? "page" : undefined}
                className={`block rounded-xl px-3 py-2 text-sm transition-colors ${s.id === current ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"}`}>{s.nav}</Link>
        ))}
      </nav>
    </>
  );
}

export default function Leadership() {
  const { stageId } = useParams();
  const { hash } = useLocation();
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const target = hash.slice(1) || null;
  const [open, setOpen] = useState({ sid: null, ids: new Set() });
  const load = () => api.get("/training/leadership").then((r) => setData(r.data)).catch((e) => setDenied(e?.response?.status === 403));
  useEffect(() => { load(); }, []);
  const current = data && (data.stages.find((s) => s.id === stageId) || data.stages[0]);
  useEffect(() => {
    if (current) setOpen({ sid: current.id, ids: new Set(topicOrder(current).slice(0, 2)) });
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data) return;
    if (!target) { window.scrollTo({ top: 0 }); return; }
    setOpen((o) => (o.ids.has(target) ? o : { ...o, ids: new Set([...o.ids, target]) }));
    const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [stageId, data, target, open.sid]);
  if (denied) return (
    <div className="max-w-md mx-auto text-center py-16 space-y-2" data-testid="lead-denied">
      <Lock className="w-8 h-8 mx-auto text-[#C9A961]" />
      <h1 className="font-serif text-2xl text-[#0A1F44]">Leadership Library</h1>
      <p className="text-sm text-slate-600">This library is available to Leaders, Team Leaders, Platinums, ATL Oversight and Admins.</p>
      <Link to="/hub" data-testid="lead-denied-back" className="inline-block text-sm text-[#0A1F44] underline underline-offset-2 pt-2">Back to Resources</Link>
    </div>
  );
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading library…</div>;
  const sec = current;
  const acc = {
    isOpen: (id) => (open.sid === sec.id ? open.ids.has(id) : topicOrder(sec).slice(0, 2).includes(id)),
    isPrimary: (id) => topicOrder(sec).slice(0, 2).includes(id),
    toggle: (id) => setOpen((o) => { const ids = new Set(o.ids); if (ids.has(id)) ids.delete(id); else ids.add(id); return { ...o, ids }; }),
  };
  return (
    <div className="space-y-6" data-testid="leadership-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <Link to="/hub" className="inline-flex items-center min-h-[44px] text-sm text-white/80 hover:text-white">← Resources</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1">Leadership Library</h1>
        <p className="text-white/70 text-sm mt-1 max-w-2xl">A reference library for coaching, culture and developing people — find the help you need, when you need it.</p>
        {user?.role === "admin" && !isViewAs && <a href={data.source_sheet} target="_blank" rel="noopener noreferrer" data-testid="lead-open-sheet"
           className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-white mt-3"><Sheet className="w-3.5 h-3.5" /> Open Google Sheet (admin)</a>}
      </section>
      <div className="grid gap-6 lg:grid-cols-[250px_1fr]">
        <SectionNav data={data} current={sec.id} />
        <article className="space-y-5 min-w-0" data-testid={`lead-section-${sec.id}`}>
          <header>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">{sec.nav}</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0A1F44] mt-1" data-testid="lead-section-title">{sec.title}</h2>
            {sec.id === "start-here" && sec.subtitle && <p className="text-sm text-slate-600 mt-1">{sec.subtitle}</p>}
          </header>
          {/coaching & developing/i.test(sec.nav) && <CallFormCard testid="lead-call-form" />}
          {sec.id === "start-here" ? <StartHere sec={sec} acc={acc} /> : <Topics sec={sec} acc={acc} />}
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600 pt-4" data-testid="lead-synced-at">
            Content last synced from the master sheet {fmtDateTime(data.synced_at)} (Melbourne)
            {user?.can_sync_content && !isViewAs && <AdminSync slug="leadership" onDone={load} />}
          </div>
        </article>
      </div>
    </div>
  );
}
